const LAST_URL_STORAGE_KEY = 'rw_last_rulewatch_url:v2';
const LAST_URL_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;
const RESTORE_LAST_URL_PARAM = '__rw_restore_last_url';

const getSafeCurrentAppUrl = (rawUrl: unknown): URL | null => {
  if (typeof rawUrl !== 'string') return null;

  try {
    const url = new URL(rawUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return null;
    if (url.origin !== window.location.origin) return null;
    if (url.username || url.password) return null;
    if (url.searchParams.has(RESTORE_LAST_URL_PARAM)) {
      url.searchParams.delete(RESTORE_LAST_URL_PARAM);
    }
    return url;
  } catch {
    return null;
  }
};

// Clear before leaving the session, including logout received in an iframe.
export const clearLastRuleWatchUrl = () => {
  try {
    window.localStorage.removeItem(LAST_URL_STORAGE_KEY);
  } catch {
    // Storage access must never prevent logout.
  }
};

export const storeCurrentRuleWatchUrl = () => {
  try {
    const url = getSafeCurrentAppUrl(window.location.href);
    if (!url) return;

    // Each origin (including localhost ports) owns its last screen.
    window.localStorage.setItem(
      LAST_URL_STORAGE_KEY,
      JSON.stringify({
        url: url.toString(),
        expiresAt: Date.now() + LAST_URL_MAX_AGE_MS,
      })
    );
  } catch {
    // A failed write must not restore an older screen on the next toggle.
    try {
      window.localStorage.removeItem(LAST_URL_STORAGE_KEY);
    } catch {
      // Storage can be disabled entirely; switching must still work.
    }
  }
};

export const getLoungeToggleUrl = (rootUrl: string) => {
  try {
    const url = new URL(rootUrl);
    if (!['http:', 'https:'].includes(url.protocol)) return rootUrl;
    url.searchParams.set(RESTORE_LAST_URL_PARAM, '1');
    return url.toString();
  } catch {
    return rootUrl;
  }
};

export const restoreCurrentRuleWatchUrl = () => {
  const entryUrl = new URL(window.location.href);
  if (entryUrl.searchParams.get(RESTORE_LAST_URL_PARAM) !== '1') return;

  // Consume the flag before routing/auth starts, even when storage is unavailable.
  entryUrl.searchParams.delete(RESTORE_LAST_URL_PARAM);
  let targetUrl = entryUrl;

  try {
    const rawState = window.localStorage.getItem(LAST_URL_STORAGE_KEY);
    const state: unknown = rawState ? JSON.parse(rawState) : null;
    if (
      state &&
      typeof state === 'object' &&
      'url' in state &&
      'expiresAt' in state
    ) {
      const savedUrl = getSafeCurrentAppUrl(state.url);
      if (
        savedUrl &&
        typeof state.expiresAt === 'number' &&
        Number.isFinite(state.expiresAt) &&
        state.expiresAt > Date.now()
      ) {
        targetUrl = savedUrl;
      }
    }
  } catch {
    // Corrupt or inaccessible state falls back to the configured entry URL.
  }

  window.history.replaceState(window.history.state, '', targetUrl.toString());
};
